<template>
  <div class="container">
    <div class="frame">
      <div class="frame__inner">
        <div class="frame__header">
          <Back 
            :title="t('confirmation_code')"
            :redirect="redirect"
          />
        </div>
        <div class="frame__body">
          <ap-loader v-if="loading" />
          <div
            v-else
            class="confirm-otp"
          >
            <div class="confirm-otp__inner">
              <div class="confirm-otp__body">
                <div class="confirm-otp__text confirm-otp__title">
                  {{ $t('otp.description', {number: phoneNumber})}}
                </div>
                <div class="confirm-otp__input">
                  <ap-input-mask
                    v-model="otpInfo.otp"
                    :label="t('otp.sms')"
                    type="otp"
                    :error-text="otpErrors"
                    @keyup="keyup"
                  />
                </div>


                <div
                  v-if="showTimer"
                  class="confirm-otp__text confirm-otp__description"
                >
                  {{$t('otp.timer', {timer: timer})}}
                </div>
                <div
                  v-else
                  class="confirm-otp__resend"
                >
                  <div class="confirm-otp__text">
                    {{$t('otp.resend')}}
                  </div>
                  <button
                    class="resend-btn"
                    @click="resendOTP"
                  >
                    {{t('resendOTP')}}
                  </button>
                </div>

                <div class="confirm-otp__btn">
                  <ap-button
                    :full="true"
                    type="primary"
                    :disable="disableBtn"
                    @click="sendOtpInfo"
                  >
                    {{t('confirm')}}
                  </ap-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { 
  REMITTAMCES_TO_CARD, 
  REMITTAMCES_TO_CARD_MERCHANT, 
  REMITTAMCES_TO_SIM, 
  SERVICES, 
  STATUS_ERROR, 
  STATUS_OK 
} from '@/constant';

import { OTP } from '@/util/interface';

import apButton from '@/components/UI/apButton.vue';
import apInputMask from '@/components/UI/apInputMask.vue';
import apLoader from '@/components/UI/apLoader.vue';
import Back from '@/components/Back.vue';

export default defineComponent({
  components: {
    Back,
    apInputMask,
    apButton,
    apLoader
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { t } = useI18n();

    const timer = ref(59);
    
    const otpInfo = ref<OTP>({
      id: '',
      phone: '',
      otp: '',
      paymentType: ''
    });

    const showTimer = ref(true);

    const redirect = ref({
      name: 'main',
      text: t('back')
    });

    const otpTypes = ref({
      [SERVICES]: 'services/confirmServicePay',
      [REMITTAMCES_TO_SIM]: 'simRemittances/confirmRemittancesToSim',
      [REMITTAMCES_TO_CARD]: 'cardRemittances/confirmRemittancesToCard',
      [REMITTAMCES_TO_CARD_MERCHANT]: 'cardRemittancesMerchant/confirmRemittancesToCard'
    });

    const resendOtpTypes = ref({
      [SERVICES]: 'services/sendOtp',
      [REMITTAMCES_TO_SIM]: 'simRemittances/resendOtp',
      [REMITTAMCES_TO_CARD]: 'cardRemittances/resendOtp',
      [REMITTAMCES_TO_CARD_MERCHANT]: 'simRemittances/resendOtp'
    });

    const redirectRoutes = ref({
      [SERVICES]: 'service-status',
      [REMITTAMCES_TO_SIM]: 'service-status',
      [REMITTAMCES_TO_CARD]: 'service-status',
      [REMITTAMCES_TO_CARD_MERCHANT]: 'card-remittances'
    });


    const error = ref({
      [SERVICES]: 'services/servicePaymentConfirmError',
      [REMITTAMCES_TO_SIM]: 'simRemittances/confirmRemittancesToSimErrors',
      [REMITTAMCES_TO_CARD]: 'cardRemittances/remittancesToCardErrors',
      [REMITTAMCES_TO_CARD_MERCHANT]: 'cardRemittancesMerchant/remittancesToCardErrors'
    });

    const clearErrors = ref({
      [SERVICES]: 'services/setServicePaymentConfirmError',
      [REMITTAMCES_TO_SIM]: 'simRemittances/setConfirmRemittancesToSimErrors',
      [REMITTAMCES_TO_CARD]: 'cardRemittances/setRemittancesToCardErrors',
      [REMITTAMCES_TO_CARD_MERCHANT]: 'cardRemittancesMerchant/setRemittancesToCardErrors'
    });



    const errorText = ref('');

    const errorList = computed(() => store.getters[error.value[`${otpInfo.value.paymentType}`]]);

    const language = computed(() => store.getters['language']);

    const loading = computed(() => store.getters['loading']);

    const otp = computed(() => store.getters['otp']);

    const phoneNumber = computed(() => otpInfo.value.phone.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '($1) ••• $3 $4'));

    const otpErrors = computed(() => {
      
      if (Array.isArray(otpInfo.value.paymentType)) {
        return;
      }

      const error = errorList.value;

      if (!error) {
        return '';
      }

      if (error?.payload?.['confirm_inputs.otp']?.[0]) {
        return error.payload?.['confirm_inputs.otp']?.[0];
      } else if (error.code && error.code.length){
        return error.code[0];
      } else {
        return error?.message || '';
      }
    });

    const disableBtn = computed(() => !(otpInfo.value.otp.length === 6));

    const keyup = () => {
      if (errorList.value && Object.keys(errorList.value).length) {
        store.commit(clearErrors.value[`${otpInfo.value.paymentType}`], {});
      }
    };
  
    const getOTPInfo = () => {
      if (!otpInfo.value.id) {
        return '';
      }

      otpTimer();
    };
    
    const resendOTP = async () => {
      if (Array.isArray(otpInfo.value.paymentType)) {
        return;
      }

      
      const data = {
        id: otpInfo.value.id,
        language: language.value
      };

      await store.dispatch(resendOtpTypes.value[`${otpInfo.value.paymentType}`], data);

      showTimer.value = true;
      timer.value = 59;
      otpTimer(); 
    };

    const sendOtpInfo = async () => {
      if (Array.isArray(otpInfo.value.paymentType)) {
        return;
      }

      const data = {
        id: otpInfo.value.id,
        confirm_inputs: {
          otp: otpInfo.value.otp
        },
        language: language.value
      };
      
     
      const otp = await store.dispatch(otpTypes.value[`${otpInfo.value.paymentType}`], data);

      if (otp.status === STATUS_ERROR && (otp.code !== 601 && otp.code !== 603 && otp.code !== 609)) {
        const statusData = {
          payment_id: data.id || otp.payment.id,
            paymentType: otpInfo.value.paymentType,
            ...otp.code && { code: otp.code},
            ...otp.status && { status: otp.status },
            ...otp.message && { failure_description: otp.message }

        }

        store.commit('setPaymentOtpData', statusData);
        router.push({ name: 'service-status',
          params: {
            ...statusData
          } 
        });
      }
      
      if (otp.status === STATUS_OK || otp.response) {
        const statusData = {
          paymentType: otpInfo.value.paymentType,
            ...(otp.payment && otp.payment.id) && { payment_id: otp.payment.id},
            ...(otp.paymentId) && { payment_id: otp.paymentId},
            ...otpInfo.value.phone && { phone: otpInfo.value.phone },
            ...otp.url && { url: otp.url },
            ...otp.message && { failure_description: otp.message }
        }
        store.commit('setPaymentOtpData', statusData);
        router.push({ name: redirectRoutes.value[`${otpInfo.value.paymentType}`],
          params: {
            ...statusData
          } 
        });
      }
    };

    const otpTimer = () => {
      const interval = setInterval(() => 
      {
        timer.value--;
        if (timer.value === 0) {
          showTimer.value = false;
          clearInterval(interval);
        }
      }, 1000);
    }; 


    onBeforeUnmount(() => {
      store.commit(clearErrors.value[`${otpInfo.value.paymentType}`], {});
    });

    onMounted(() => {
      if (Object.keys(otp.value).length !== 0){
        otpInfo.value = {...otp.value, id: otp.value.service_id };
      }
      getOTPInfo();
    });

    return {
      sendOtpInfo,
      otpInfo,
      errorText,
      phoneNumber,
      timer,
      showTimer,
      resendOTP,
      otpErrors,
      // composeErrorText
      disableBtn,
      redirect,
      keyup,
      loading,
      t
    };
  },
});
</script>
<style lang="scss" scoped>
.confirm-otp{
  &__inner{
    padding: 72px 0 84px 0;
  }
  &__body{
    max-width: 448px;
    width: 100%;
    margin: 0 auto;
  }
  &__title{
    margin-bottom: 40px;
  }
  &__text{
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.72);
  }
  &__description{
    margin-bottom: 42px;
  }
  &__input{
    margin-bottom: 28px;
  }
  &__resend{
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 160px;
  }
}
.resend-btn{
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  padding: 11px 16px;
  color: $primary-purple;
  max-width: 180px;
  width: 100%;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 21px;
}
.altel{
  .resend-btn{
    color: $primary-pink;
  }
}
.tele2{
  .resend-btn{
    color: $primary-black;
  }
}
@media (min-width: 320px) and (max-width: 480px) {
  .confirm-otp{
    &__inner{
      padding: 34px 16px 24px 16px;
    }
    &__title{
      width: 100%;
    }
    &__description{
      margin-bottom: 200px;
    }
  }
}
</style>
